// "Integrations & features" — the capability gate, made visible and adjustable.
//
// Three things, in the order a biller asks them:
//   1. WHICH SOURCE this practice has, and the reason the registry picked it. Before
//      this panel the answer lived across three vendor dropdowns and was inferred
//      differently by every page.
//   2. WHAT THIS PRACTICE'S FEEDS SUPPLY — the capability chips (schedule, coverages,
//      auths, charges, statuses, remits). NOT the source alone: an integrated
//      clearinghouse contributes statuses and remits, and a scheduling EMR contributes
//      the schedule. Vortex is all three at once — PracticeSuite CSV for claims, Tebra
//      for the diary — so labelling these "what the source supplies" would have the
//      panel attribute a Tebra schedule to a CSV export.
//   3. WHICH FEATURES are on, each with a tri-state control: Auto (computed from the
//      capabilities), On, Off. Auto is the default and is what every practice has
//      until someone deliberately overrides it.
//
// Overrides are admin-only (the backend enforces it; the controls are hidden
// otherwise). Forcing a feature ON does not invent data — it shows a page that may
// have little in it — so the control says so rather than implying a connection.

import React, { useState } from 'react';
import useCapabilities, { primeCapabilities } from '../hooks/useCapabilities';
import SheetConnectorCard from '../features/sheet/SheetConnectorCard';
import './IntegrationsFeaturesPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Display order and wording. Grouped the way the work is done: intake, then billing,
// then follow-up. A feature missing from here still renders (falls back to its key),
// so a new backend feature is visible the day it ships rather than invisible until
// someone remembers this list — but IntegrationsFeaturesPanel.test.js fails on any
// backend key (__fixtures__/feature-keys.json) without a label, so it is not shown raw.
export const FEATURE_LABELS = [
  ['roster', 'Upcoming roster'],
  ['eligibility', 'Eligibility'],
  ['copay', 'Copay estimates'],
  ['patients', 'Patients'],
  ['authorizations', 'Authorizations'],
  ['charge_capture', 'Charge capture'],
  ['charge_templates', 'Charge templates'],
  ['auto_coder', 'Auto-coder (drafts yesterday\'s visits for PM entry)'],
  ['cobalt_fee_schedules', 'Cobalt fee schedules (weekly pull of eCW fee schedules)'],
  ['claim_editor', 'Claim editor'],
  ['fee_schedule', 'Fee schedule'],
  ['rules', 'Rules'],
  ['worklist', 'Denial worklist'],
  ['writeoffs', 'Write-offs'],
  ['statements', 'Patient statements'],
  ['collections', 'Collections'],
  ['exports', 'CSV exports'],
  ['reports', 'Reports'],
  ['submit_queue', 'Submit queue'],
  ['auth_advisor', 'Auth advisor'],
  ['roster_send', 'Send roster to practice'],
  ['note_viewer', 'Visit notes'],
  ['coding_assist', 'Coding assist (suggestions)'],
  ['data_page', 'Data page'],
  ['worklist_import', 'Worklist import (biller sheet, Waterfall page)'],
  ['card_charging', 'Card charging (Atlas-controlled)'],
];

const CAPABILITY_LABELS = {
  schedule: 'Schedule',
  coverages: 'Coverages',
  auths: 'Authorizations',
  charges: 'Charges',
  statuses: 'Claim statuses',
  remits: 'Remittances',
};

const ALL_CAPABILITIES = ['schedule', 'coverages', 'auths', 'charges', 'statuses', 'remits'];

function orderedFeatures(features) {
  const known = FEATURE_LABELS.filter(([key]) => key in features);
  const extra = Object.keys(features)
    .filter((key) => !FEATURE_LABELS.some(([k]) => k === key))
    .map((key) => [key, key]);
  return [...known, ...extra];
}

// `switchesOnly` (Practice Details' Features tab, 2026-10-25): just the feature table
// under the title "Feature switches". The source, the capability chips and the sheet
// connector are left out there: the Integrations tab's Data table and its own sheet
// card say the same things.
export default function IntegrationsFeaturesPanel({ practiceId, practice, isAdmin, switchesOnly = false }) {
  // Deliberately the SAME hook the sidebar and every gated page read, not a private
  // fetch. Saving credentials three inches above this panel calls
  // invalidateCapabilities(), and because this shares that cache the panel and the
  // sidebar update together instead of disagreeing until a reload.
  const gate = useCapabilities(practiceId);
  const title = switchesOnly ? 'Feature switches' : 'Integrations & features';
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(null);

  const setOverride = async (feature, value) => {
    setSaving(feature);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/practices/${practiceId}/features`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [feature]: value }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.error || 'Could not save that change.' });
        return;
      }
      // The sidebar and every open page read the hook's cache, so writing the
      // response back into it is what makes a page appear or disappear immediately —
      // and it re-renders this panel, since it reads the same cache.
      primeCapabilities(practiceId, await response.json());
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not save that change.' });
    } finally {
      setSaving(null);
    }
  };

  if (gate.loading) {
    return (
      <div className="integrations-section">
        <h3>{title}</h3>
        <p className="gate-empty">Loading…</p>
      </div>
    );
  }
  if (gate.error) {
    return (
      <div className="integrations-section">
        <h3>{title}</h3>
        <div className="field-message error">{gate.error}</div>
      </div>
    );
  }

  const { capabilities, overrides, computed, features } = gate;

  return (
    <div className="integrations-section">
      <h3>{title}</h3>

      {message && (
        <div className={`field-message ${message.type}`} style={{ marginBottom: 12 }}>
          {message.text}
          <button className="message-close-sm" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {!switchesOnly && (
      <>
      <div className="gate-source">
        <div className="gate-source-row">
          <span className="detail-label">Data source</span>
          <span className="gate-source-value">
            {gate.source && gate.source.key ? (
              <>
                <strong>{gate.source.label}</strong>
                <code className="gate-source-key">{gate.source.key}</code>
              </>
            ) : (
              <em>No recognized data source</em>
            )}
          </span>
        </div>
        <div className="gate-source-reason">{gate.source ? gate.source.reason : ''}</div>
      </div>

      {/* The union of every feed, not the source's own set — see the header. */}
      <div className="gate-chips">
        {ALL_CAPABILITIES.map((cap) => (
          <span
            key={cap}
            className={`gate-chip${capabilities.includes(cap) ? ' on' : ''}`}
            title={
              capabilities.includes(cap)
                ? `Something connected to this practice supplies ${CAPABILITY_LABELS[cap].toLowerCase()}`
                : `Nothing connected to this practice supplies ${CAPABILITY_LABELS[cap].toLowerCase()}`
            }
          >
            {CAPABILITY_LABELS[cap]}
          </span>
        ))}
      </div>

      <SheetConnectorCard practice={practice} source={gate.source} onChanged={gate.refresh} />
      </>
      )}

      <table className="detail-table gate-table">
        <thead>
          <tr>
            <th className="gate-col-feature">Feature</th>
            <th className="gate-col-state">State</th>
            {isAdmin && <th className="gate-col-control">Override</th>}
          </tr>
        </thead>
        <tbody>
          {orderedFeatures(features).map(([key, label]) => {
            const override = key in overrides ? overrides[key] : null;
            const on = features[key];
            return (
              <tr key={key}>
                <td>{label}</td>
                <td>
                  <span className={`gate-state${on ? ' on' : ''}`}>{on ? 'On' : 'Off'}</span>
                  {override !== null && <span className="gate-forced">forced</span>}
                  {override === null && (
                    <span className="gate-auto-note">
                      {computed[key] ? 'available from this source' : 'not supported by this source'}
                    </span>
                  )}
                </td>
                {isAdmin && (
                  <td>
                    <div className="gate-tristate" role="group" aria-label={`${label} override`}>
                      {[
                        ['Auto', null],
                        ['On', true],
                        ['Off', false],
                      ].map(([text, value]) => (
                        <button
                          key={text}
                          type="button"
                          className={`gate-tristate-btn${override === value ? ' active' : ''}`}
                          disabled={saving === key}
                          onClick={() => setOverride(key, value)}
                        >
                          {text}
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {isAdmin && (
        <p className="gate-footnote">
          <strong>Auto</strong> follows the data source above. <strong>On</strong> shows a feature
          the source does not supply — useful when the data arrives another way, but the page may
          have little in it. <strong>Off</strong> hides a feature from everyone at this practice.
        </p>
      )}
    </div>
  );
}
